import { Login } from 'persistable-types';

import { LoginVaultPermissions } from './menuStructure/getLoginVaultMetadata';

export interface LoginPermissions {
  editLogin: boolean;
  deleteLogin: boolean;
  moveLogin: boolean;
  viewSecrets: boolean;
  useLogin: boolean;
  viewHistory: boolean;
  duplicateLogin: boolean;
  shareLoginExternal: boolean;
  viewLoginStatus: boolean;
}

export const ALL_LOGIN_PERMISSIONS: LoginPermissions = {
  editLogin: true,
  deleteLogin: true,
  moveLogin: true,
  viewSecrets: true,
  useLogin: true,
  viewHistory: true,
  duplicateLogin: true,
  shareLoginExternal: true,
  viewLoginStatus: true,
};

export const NO_LOGIN_PERMISSIONS: LoginPermissions = {
  editLogin: false,
  deleteLogin: false,
  moveLogin: false,
  viewSecrets: false,
  useLogin: false,
  viewHistory: false,
  duplicateLogin: false,
  shareLoginExternal: false,
  viewLoginStatus: false,
};

// This method is the source of truth for login permissions
export default function getLoginPermissions(
  login: Pick<Login, 'linkedFrom' | 'linkedTo' | 'linkedViewSecrets' | 'uiType'>,
  vaultPermissions: LoginVaultPermissions,
): LoginPermissions {
  if (!vaultPermissions.viewVault) {
    return NO_LOGIN_PERMISSIONS;
  }

  const editLogin = canEditLogin(login, vaultPermissions);
  const viewSecrets =
    vaultPermissions.viewSecrets && (!login.linkedFrom || !!login.linkedViewSecrets);
  const shareLoginExternal = editLogin && viewSecrets;

  return {
    editLogin,
    deleteLogin: vaultPermissions.deleteLogins,
    moveLogin:
      vaultPermissions.deleteLogins &&
      // logins can only be moved if they have the viewSecrets permissions
      // as otherwise the secrets could be seen in the target vault
      vaultPermissions.viewSecrets &&
      // linked child logins can never be moved
      !login.linkedFrom,
    viewSecrets,
    useLogin: vaultPermissions.useLogins,
    viewHistory: vaultPermissions.viewHistory,
    duplicateLogin: vaultPermissions.createLogins && !login.linkedFrom,
    shareLoginExternal,
    viewLoginStatus: vaultPermissions.viewLoginStatus && !login.linkedFrom,
  };
}

export function canEditLogin(
  login: Pick<Login, 'linkedFrom'>,
  vaultPermissions: Pick<LoginVaultPermissions, 'editLogins'>,
) {
  return vaultPermissions.editLogins && !login.linkedFrom;
}
